import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import StudentAssignment from '@/components/StudentAssignment';
import { toast } from 'sonner';
import { Plus, FileText, Trophy, ExternalLink, Link2, Save, ClipboardList, Users, UserPlus, Clock } from 'lucide-react';
import axios from 'axios';
import InlineEdit from '@/components/InlineEdit';
import ResourceReuseModal from '@/components/shared/ResourceReuseModal';
import DeleteContentMenu from '@/components/shared/DeleteContentMenu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function TeacherScoreInline({ item, classMembers, token, t, onScoreSet }) {
  const [selectedStudent, setSelectedStudent] = useState('');
  const [scoreValue, setScoreValue] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSetScore = async () => {
    if (!selectedStudent || !scoreValue.trim()) return;
    setSaving(true);
    try {
      await axios.post(`${API}/content/${item.id}/score`, {
        student_id: selectedStudent, score: scoreValue.trim()
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t('success'));
      setSelectedStudent('');
      setScoreValue('');
      onScoreSet();
    } catch { toast.error(t('error')); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3" data-testid={`score-section-${item.id}`}>
      <p className="text-sm font-bold text-amber-700 flex items-center gap-1.5">
        <Trophy className="w-4 h-4" /> {t('evaluation_result')}
      </p>

      {/* Existing scores */}
      {item.scores?.length > 0 && (
        <div className="space-y-1.5 mb-3">
          {item.scores.map(s => {
            const member = classMembers.find(m => m.user_id === s.student_id);
            return (
              <div key={s.id} className="flex items-center justify-between text-sm bg-white rounded-lg px-3 py-2 border border-amber-100">
                <span className="font-medium text-slate-700">{member ? `${member.first_name} ${member.last_name}` : s.student_id}</span>
                <Badge className="bg-amber-100 text-amber-700 font-bold">{s.score}</Badge>
              </div>
            );
          })}
        </div>
      )}

      {/* Set new score */}
      <div className="flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-amber-600">{t('student')}</Label>
          <Select value={selectedStudent} onValueChange={setSelectedStudent}>
            <SelectTrigger className="bg-white" data-testid={`score-student-${item.id}`}>
              <SelectValue placeholder={t('search_students')} />
            </SelectTrigger>
            <SelectContent>
              {classMembers.map(m => (
                <SelectItem key={m.user_id} value={m.user_id}>{m.first_name} {m.last_name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[140px]">
          <Label className="text-xs text-amber-600">{t('score')}</Label>
          <Input
            value={scoreValue}
            onChange={e => setScoreValue(e.target.value)}
            placeholder="10/10, A+, 95%..."
            className="bg-white"
            data-testid={`score-value-${item.id}`}
          />
        </div>
        <Button
          size="sm"
          disabled={saving || !selectedStudent || !scoreValue.trim()}
          onClick={handleSetScore}
          className="gap-1 bg-amber-500 hover:bg-amber-600 rounded-lg"
          data-testid={`score-submit-${item.id}`}
        >
          {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Save className="w-3.5 h-3.5" /> {t('save')}</>}
        </Button>
      </div>
    </div>
  );
}

export default function EvaluationsTab({ classId }) {
  const { user, token } = useAuth();
  const { t } = useLanguage();
  const headers = { headers: { Authorization: `Bearer ${token}` } };
  const isStudent = user?.role === 'student';

  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [classMembers, setClassMembers] = useState([]);
  const [reuseItem, setReuseItem] = useState(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [evalLink, setEvalLink] = useState('');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const fetchItems = useCallback(async () => {
    try {
      const [contentRes, membersRes] = await Promise.all([
        axios.get(`${API}/content/list/${classId}/evaluation`, headers),
        !isStudent ? axios.get(`${API}/classes/${classId}/members`, headers) : Promise.resolve({ data: [] })
      ]);
      setItems(contentRes.data);
      if (!isStudent) {
        setClassMembers(membersRes.data.filter(m => m.role === 'student'));
      }
    } catch (err) { console.error("[EvaluationsTab] silent error:", err); }
  }, [classId, token, isStudent]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleCreate = async () => {
    if (!title.trim()) return;
    try {
      const assigned_to = selectedStudents.length > 0 ? selectedStudents : null;
      const scheduled_for = (scheduleEnabled && scheduledDate && scheduledTime)
        ? new Date(`${scheduledDate}T${scheduledTime}`).toISOString() : null;
      await axios.post(`${API}/content/create`, {
        class_id: classId, content_type: 'evaluation', title, description, evaluation_link: evalLink, assigned_to, scheduled_for
      }, headers);
      toast.success(scheduleEnabled ? t('content_scheduled') : t('success'));
      setShowCreate(false);
      setTitle(''); setDescription(''); setEvalLink(''); setSelectedStudents([]);
      setScheduleEnabled(false); setScheduledDate(''); setScheduledTime('');
      fetchItems();
    } catch { toast.error(t('error')); }
  };

  const handleDelete = async (contentId) => {
    try {
      await axios.delete(`${API}/content/delete/${contentId}`, headers);
      toast.success(t('success'));
      fetchItems();
    } catch { toast.error(t('error')); }
  };

  return (
    <div data-testid="evaluations-tab">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-slate-700" style={{ fontFamily: 'Montserrat' }}>{t('evaluations')}</h3>
        {!isStudent && (
          <Button data-testid="create-evaluation-btn" onClick={() => setShowCreate(true)} size="sm" className="gap-1 rounded-lg bg-red-500 hover:bg-red-600">
            <Plus className="w-4 h-4" /> {t('create')}
          </Button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-center py-12 text-slate-400"><ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-50" /><p>{t('no_items')}</p></div>
      ) : (
        <div className="space-y-5">
          {items.map(item => (
            <div key={item.id} className="content-card bg-white rounded-xl p-5" data-testid={`evaluation-${item.id}`}>
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-bold text-slate-800" style={{ fontFamily: 'Montserrat' }}>{item.title}</h4>
                  <div className="mt-1">
                    <InlineEdit
                      value={item.description || ''}
                      canEdit={!isStudent}
                      placeholder="Sin instrucciones"
                      onSave={async (text) => {
                        try {
                          await axios.patch(`${API}/content/instructions/${item.id}`, { description: text }, headers);
                          setItems(prev => prev.map(p => p.id === item.id ? { ...p, description: text } : p));
                          toast.success(t('success'));
                        } catch (err) {
                          toast.error(t('error'));
                          throw err;
                        }
                      }}
                    />
                  </div>
                  {/* Individual assignment indicator */}
                  {!isStudent && item.assigned_to && item.assigned_to.length > 0 && (
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200 mt-2">
                      <Users className="w-3 h-3 mr-1" />
                      {item.assigned_to.length} estudiante(s)
                    </Badge>
                  )}
                </div>
                {!isStudent && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => setReuseItem(item)}
                      className="text-slate-300 hover:text-emerald-500 transition-colors"
                      title="Asignar a otros estudiantes"
                      data-testid={`reuse-${item.id}`}>
                      <UserPlus className="w-4 h-4" />
                    </button>
                    <DeleteContentMenu
                      contentId={item.id}
                      classStudents={classMembers.filter(m => m.role === 'student')}
                      token={token}
                      onDelete={handleDelete}
                      onUnassigned={fetchItems}
                      testIdPrefix="delete-evaluation"
                    />
                  </div>
                )}
              </div>

              {/* === CASILLA: Enlace a plataforma de evaluacion === */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4" data-testid={`eval-link-box-${item.id}`}>
                <p className="text-sm font-bold text-red-700 flex items-center gap-1.5 mb-2">
                  <Link2 className="w-4 h-4" /> {t('evaluation_link')}
                </p>
                {item.evaluation_link ? (
                  <a
                    href={item.evaluation_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-white text-red-600 rounded-lg text-sm font-medium hover:bg-red-100 transition-colors border border-red-200"
                    data-testid={`eval-link-${item.id}`}
                  >
                    <ExternalLink className="w-4 h-4" />
                    {item.evaluation_link.length > 60 ? item.evaluation_link.substring(0, 60) + '...' : item.evaluation_link}
                  </a>
                ) : (
                  <p className="text-sm text-red-400 italic">No hay enlace disponible</p>
                )}
              </div>

              {/* === CASILLA: Resultado de la evaluacion (Estudiante) === */}
              {isStudent && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid={`eval-result-box-${item.id}`}>
                  <p className="text-sm font-bold text-amber-700 flex items-center gap-1.5 mb-2">
                    <Trophy className="w-4 h-4" /> {t('evaluation_result')}
                  </p>
                  {item.my_score ? (
                    <div className="flex items-center gap-3">
                      <div className="bg-white border border-amber-200 rounded-lg px-5 py-3">
                        <span className="text-2xl font-black text-amber-600" style={{ fontFamily: 'Montserrat' }}>
                          {item.my_score.score}
                        </span>
                      </div>
                      <p className="text-sm text-amber-600">Puntuacion asignada por el docente</p>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-400 italic">Pendiente de calificacion</p>
                  )}
                </div>
              )}

              {/* === CASILLA: Resultado de la evaluacion (Docente - asignar puntuacion) === */}
              {!isStudent && (
                <TeacherScoreInline
                  item={item}
                  classMembers={classMembers}
                  token={token}
                  t={t}
                  onScoreSet={fetchItems}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Create Evaluation Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Montserrat' }}>{t('create')} {t('evaluations')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>{t('title')}</Label>
              <Input
                data-testid="eval-title-input"
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder="Ej: Evaluacion Unidad 3"
              />
            </div>
            <div>
              <Label>{t('evaluation_link')}</Label>
              <Input
                data-testid="eval-link-input"
                value={evalLink}
                onChange={e => setEvalLink(e.target.value)}
                placeholder="https://forms.google.com/..."
              />
              <p className="text-xs text-slate-400 mt-1">Pega el enlace de la plataforma de evaluacion externa</p>
            </div>
            <div>
              <Label>{t('description')}</Label>
              <Textarea
                data-testid="eval-desc-input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Instrucciones para el estudiante..."
                rows={3}
              />
            </div>
            {/* Student Assignment */}
            {!isStudent && classMembers.length > 0 && (
              <StudentAssignment
                students={classMembers}
                selectedStudents={selectedStudents}
                setSelectedStudents={setSelectedStudents}
                label="Asignar a Estudiantes"
              />
            )}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <Label className="text-sm font-bold text-slate-700 flex items-center gap-1.5">
                  <Clock className="w-4 h-4 text-blue-500" /> {t('schedule_publication')}
                </Label>
                <div className="flex items-center gap-2">
                  <button type="button" onClick={() => setScheduleEnabled(false)}
                    className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${!scheduleEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                    data-testid="evaluation-publish-now-btn">{t('publish_now')}</button>
                  <button type="button" onClick={() => setScheduleEnabled(true)}
                    className={`px-3 py-1 text-xs rounded-full font-medium transition-colors ${scheduleEnabled ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-600 hover:bg-slate-300'}`}
                    data-testid="evaluation-schedule-later-btn">{t('publish_later')}</button>
                </div>
              </div>
              {scheduleEnabled && (
                <div className="flex gap-3 mt-2">
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 mb-1 block">Fecha</Label>
                    <Input type="date" value={scheduledDate} onChange={e => setScheduledDate(e.target.value)}
                      className="bg-white" data-testid="evaluation-schedule-date-input" min={new Date().toISOString().split('T')[0]} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs text-slate-500 mb-1 block">Hora</Label>
                    <Input type="time" value={scheduledTime} onChange={e => setScheduledTime(e.target.value)}
                      className="bg-white" data-testid="evaluation-schedule-time-input" />
                  </div>
                </div>
              )}
            </div>
            <Button data-testid="eval-create-submit" onClick={handleCreate} className="w-full bg-red-500 hover:bg-red-600">{t('create')}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ResourceReuseModal
        open={!!reuseItem}
        onClose={() => setReuseItem(null)}
        resourceTitle={reuseItem?.title}
        contentId={reuseItem?.id}
        token={token}
        scheduledAssignments={reuseItem?.scheduled_assignments || []}
        classStudents={classMembers}
        currentAssignedTo={reuseItem?.assigned_to}
        onAssign={async (ids, scheduledFor) => {
          const res = await axios.post(`${API}/content/${reuseItem.id}/assign-students`, { student_ids: ids, scheduled_for: scheduledFor }, headers);
          return res.data;
        }}
        onAssigned={fetchItems}
      />
    </div>
  );
}
